import { useEffect, useState, useRef } from "react";

const API_BOOKINGS = "https://adminbackend.cctvpartner.com/appointmentbookings";
const API_TASKS = "https://adminbackend.cctvpartner.com/assigntasks";
const API_ADS = "https://adminbackend.cctvpartner.com/advertisements";

const fmt = (v) => (v != null && v !== "" ? String(v) : "—");
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
};
const isToday = (d) => {
  if (!d) return false;
  try {
    return new Date(d).toDateString() === new Date().toDateString();
  } catch {
    return false;
  }
};
const isThisWeek = (d) => {
  if (!d) return false;
  try {
    const diff = (new Date() - new Date(d)) / 86400000;
    return diff >= 0 && diff <= 7;
  } catch {
    return false;
  }
};

// ── FIELD DETECTORS ──
function getBookingFields(list) {
  const s = list[0] || {};
  return {
    nameKey:
      [
        "name",
        "customerName",
        "customer_name",
        "client_name",
        "fullName",
        "full_name",
      ].find((k) => k in s) || "name",
    phoneKey:
      [
        "phone",
        "mobile",
        "contact",
        "phoneNumber",
        "phone_number",
        "mobileNumber",
      ].find((k) => k in s) || "phone",
    dateKey:
      [
        "appointmentDate",
        "date",
        "bookingDate",
        "appointment_date",
        "scheduledAt",
        "createdAt",
        "created_at",
      ].find((k) => k in s) || "createdAt",
    statusKey:
      [
        "status",
        "bookingStatus",
        "appointment_status",
        "booking_status",
        "appointmentStatus",
      ].find((k) => k in s) || "status",
    serviceKey:
      [
        "service",
        "serviceType",
        "service_type",
        "category",
        "type",
        "serviceCategory",
      ].find((k) => k in s) || "service",
    addrKey:
      ["address", "location", "city", "area", "locality", "place"].find(
        (k) => k in s,
      ) || "address",
  };
}
function getTaskFields(list) {
  const s = list[0] || {};
  return {
    taskKey:
      [
        "task",
        "taskName",
        "task_name",
        "title",
        "description",
        "work",
        "taskTitle",
      ].find((k) => k in s) ||
      Object.keys(s)[0] ||
      "task",
    techKey:
      [
        "technician",
        "technicianName",
        "assignedTo",
        "assigned_to",
        "engineer",
        "worker",
        "staff",
        "employee",
        "name",
      ].find((k) => k in s) || "technician",
    statusKey:
      ["status", "taskStatus", "task_status", "state", "progress"].find(
        (k) => k in s,
      ) || "status",
    dateKey:
      [
        "date",
        "assignedDate",
        "assigned_date",
        "createdAt",
        "created_at",
        "scheduledDate",
        "dueDate",
        "deadline",
      ].find((k) => k in s) || "createdAt",
    addrKey:
      [
        "address",
        "location",
        "city",
        "area",
        "place",
        "locality",
        "customerAddress",
      ].find((k) => k in s) || "address",
    phoneKey:
      [
        "phone",
        "mobile",
        "contact",
        "phoneNumber",
        "customerPhone",
        "phone_number",
      ].find((k) => k in s) || "phone",
  };
}
function getAdFields(list) {
  const s = list[0] || {};
  return {
    titleKey:
      [
        "title",
        "adTitle",
        "ad_title",
        "name",
        "heading",
        "advertisement",
        "subject",
      ].find((k) => k in s) ||
      Object.keys(s)[0] ||
      "title",
    descKey:
      [
        "description",
        "desc",
        "content",
        "body",
        "text",
        "details",
        "message",
      ].find((k) => k in s) || "description",
    statusKey:
      ["status", "adStatus", "ad_status", "state", "active", "isActive"].find(
        (k) => k in s,
      ) || "status",
    dateKey:
      [
        "date",
        "createdAt",
        "created_at",
        "startDate",
        "start_date",
        "publishedAt",
        "postedAt",
        "expiryDate",
      ].find((k) => k in s) || "createdAt",
    typeKey:
      [
        "type",
        "adType",
        "ad_type",
        "category",
        "platform",
        "medium",
        "channel",
      ].find((k) => k in s) || "type",
    targetKey:
      [
        "target",
        "targetAudience",
        "target_audience",
        "audience",
        "location",
        "city",
        "area",
        "region",
      ].find((k) => k in s) || "target",
    budgetKey:
      ["budget", "cost", "amount", "price", "spend", "value"].find(
        (k) => k in s,
      ) || "budget",
    imageKey:
      [
        "image",
        "imageUrl",
        "image_url",
        "thumbnail",
        "photo",
        "banner",
        "img",
      ].find((k) => k in s) || "image",
  };
}

// ── COLORS ──
const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#db2777",
];
const STATUS_COLORS = {
  confirmed: "#16a34a",
  pending: "#d97706",
  cancelled: "#dc2626",
  completed: "#2563eb",
  scheduled: "#7c3aed",
  assigned: "#0891b2",
  done: "#16a34a",
  open: "#d97706",
  closed: "#64748b",
  inprogress: "#7c3aed",
  active: "#16a34a",
  inactive: "#64748b",
  expired: "#dc2626",
  draft: "#d97706",
  published: "#16a34a",
  paused: "#f59e0b",
};
const STATUS_META = {
  confirmed: { color: "#16a34a", bg: "#dcfce7", dot: "#22c55e" },
  pending: { color: "#d97706", bg: "#fef9c3", dot: "#f59e0b" },
  cancelled: { color: "#dc2626", bg: "#fee2e2", dot: "#ef4444" },
  completed: { color: "#2563eb", bg: "#dbeafe", dot: "#3b82f6" },
  scheduled: { color: "#7c3aed", bg: "#f5f3ff", dot: "#8b5cf6" },
  assigned: { color: "#0891b2", bg: "#ecfeff", dot: "#06b6d4" },
  done: { color: "#16a34a", bg: "#dcfce7", dot: "#22c55e" },
  open: { color: "#d97706", bg: "#fef9c3", dot: "#f59e0b" },
  closed: { color: "#64748b", bg: "#f1f5f9", dot: "#94a3b8" },
  inprogress: { color: "#7c3aed", bg: "#f5f3ff", dot: "#8b5cf6" },
  active: { color: "#16a34a", bg: "#dcfce7", dot: "#22c55e" },
  inactive: { color: "#64748b", bg: "#f1f5f9", dot: "#94a3b8" },
  expired: { color: "#dc2626", bg: "#fee2e2", dot: "#ef4444" },
  draft: { color: "#d97706", bg: "#fef9c3", dot: "#f59e0b" },
  published: { color: "#16a34a", bg: "#dcfce7", dot: "#22c55e" },
  paused: { color: "#f59e0b", bg: "#fffbeb", dot: "#fbbf24" },
};
function getStatusMeta(v) {
  const k = String(v).toLowerCase().replace(/\s/g, "");
  return (
    STATUS_META[k] ||
    STATUS_META[String(v).toLowerCase()] || {
      color: "#64748b",
      bg: "#f1f5f9",
      dot: "#94a3b8",
    }
  );
}

// ── QUERY ENGINES ──
function answerBookingQuery(query, bookings) {
  const q = query.toLowerCase().trim();
  if (!bookings.length) return null;
  const F = getBookingFields(bookings);
  const { nameKey, phoneKey, dateKey, statusKey, serviceKey, addrKey } = F;
  const gs = (b) => String(b[statusKey] || "").toLowerCase();
  const confirmed = bookings.filter((b) => /confirm/i.test(gs(b)));
  const pending = bookings.filter((b) => /pend/i.test(gs(b)));
  const cancelled = bookings.filter((b) => /cancel/i.test(gs(b)));
  const completed = bookings.filter((b) => /complet/i.test(gs(b)));
  const todayList = bookings.filter((b) => isToday(b[dateKey]));
  const weekList = bookings.filter((b) => isThisWeek(b[dateKey]));
  const row = (b) => ({
    Name: fmt(b[nameKey]),
    Phone: fmt(b[phoneKey]),
    Service: fmt(b[serviceKey]),
    Date: fmtDate(b[dateKey]),
    Status: fmt(b[statusKey]),
  });

  if (q.match(/graph|chart|compare|visual|pie|bar|trend|analyt/)) {
    const sMap = {},
      svMap = {},
      mMap = {};
    bookings.forEach((b) => {
      const s = fmt(b[statusKey]);
      sMap[s] = (sMap[s] || 0) + 1;
    });
    bookings.forEach((b) => {
      const s = fmt(b[serviceKey]);
      svMap[s] = (svMap[s] || 0) + 1;
    });
    bookings.forEach((b) => {
      try {
        const d = new Date(b[dateKey]);
        if (!isNaN(d)) {
          const k = d.toLocaleDateString("en-IN", {
            month: "short",
            year: "2-digit",
          });
          mMap[k] = (mMap[k] || 0) + 1;
        }
      } catch {}
    });
    return {
      type: "graphCompare",
      text: "Booking Analytics",
      statusData: Object.entries(sMap).sort((a, b) => b[1] - a[1]),
      serviceData: Object.entries(svMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 7),
      monthData: Object.entries(mMap).slice(-8),
      total: bookings.length,
      serviceLabel: "Service",
    };
  }
  if (q.match(/summary|total|overview|एकूण/)) {
    const sm = {};
    bookings.forEach((b) => {
      const sv = fmt(b[serviceKey]);
      sm[sv] = (sm[sv] || 0) + 1;
    });
    const top = Object.entries(sm).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
    return {
      type: "summary",
      text: "Appointment Summary",
      stats: [
        {
          label: "Total",
          val: bookings.length,
          color: "#2563eb",
          bg: "#eff6ff",
          icon: "📅",
        },
        {
          label: "Confirmed",
          val: confirmed.length,
          color: "#16a34a",
          bg: "#f0fdf4",
          icon: "✅",
        },
        {
          label: "Pending",
          val: pending.length,
          color: "#d97706",
          bg: "#fffbeb",
          icon: "⏳",
        },
        {
          label: "Cancelled",
          val: cancelled.length,
          color: "#dc2626",
          bg: "#fef2f2",
          icon: "❌",
        },
        {
          label: "Completed",
          val: completed.length,
          color: "#7c3aed",
          bg: "#f5f3ff",
          icon: "🏁",
        },
        {
          label: "Today",
          val: todayList.length,
          color: "#0891b2",
          bg: "#ecfeff",
          icon: "📆",
        },
      ],
      extra: `Top Service: ${top} · This Week: ${weekList.length}`,
    };
  }
  if (q.match(/today|आज/))
    return todayList.length
      ? {
          text: "Today's Appointments",
          badge: `${todayList.length}`,
          badgeColor: "#2563eb",
          rows: todayList.map(row),
          headers: ["Name", "Phone", "Service", "Date", "Status"],
        }
      : { text: "आज कोणतेही appointments नाहीत." };
  if (q.match(/week/))
    return {
      text: "This Week",
      badge: `${weekList.length}`,
      badgeColor: "#7c3aed",
      rows: weekList.slice(0, 15).map(row),
      headers: ["Name", "Phone", "Service", "Date", "Status"],
    };
  if (q.match(/confirm/) && !q.match(/not/))
    return {
      text: "Confirmed",
      badge: `${confirmed.length}`,
      badgeColor: "#16a34a",
      rows: confirmed.slice(0, 15).map(row),
      headers: ["Name", "Phone", "Service", "Date", "Status"],
    };
  if (q.match(/pend/))
    return {
      text: "Pending",
      badge: `${pending.length}`,
      badgeColor: "#d97706",
      rows: pending.slice(0, 15).map(row),
      headers: ["Name", "Phone", "Service", "Date", "Status"],
    };
  if (q.match(/cancel/))
    return {
      text: "Cancelled",
      badge: `${cancelled.length}`,
      badgeColor: "#dc2626",
      rows: cancelled
        .slice(0, 15)
        .map((b) => ({
          Name: fmt(b[nameKey]),
          Phone: fmt(b[phoneKey]),
          Service: fmt(b[serviceKey]),
          Date: fmtDate(b[dateKey]),
        })),
      headers: ["Name", "Phone", "Service", "Date"],
    };
  if (q.match(/complet/))
    return {
      text: "Completed",
      badge: `${completed.length}`,
      badgeColor: "#7c3aed",
      rows: completed.slice(0, 15).map(row),
      headers: ["Name", "Phone", "Service", "Date", "Status"],
    };
  if (q.match(/service|cctv|install|camera|repair/)) {
    const map = {};
    bookings.forEach((b) => {
      const s = fmt(b[serviceKey]);
      map[s] = (map[s] || 0) + 1;
    });
    const e = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return {
      text: "Service Breakdown",
      rows: e.map(([s, c]) => ({ Service: s, Count: c })),
      headers: ["Service", "Count"],
      type: "chart",
      chartData: e.slice(0, 7),
    };
  }
  if (q.match(/location|city|area|address/)) {
    const map = {};
    bookings.forEach((b) => {
      const a = fmt(b[addrKey]);
      if (a !== "—") map[a] = (map[a] || 0) + 1;
    });
    return {
      text: "Location Breakdown",
      rows: Object.entries(map)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([l, c]) => ({ Location: l, Count: c })),
      headers: ["Location", "Count"],
    };
  }
  if (q.match(/recent|latest|new/)) {
    const sorted = [...bookings].sort(
      (a, b) => new Date(b[dateKey]) - new Date(a[dateKey]),
    );
    return {
      text: "Recent Appointments",
      badge: "10",
      badgeColor: "#2563eb",
      rows: sorted.slice(0, 10).map(row),
      headers: ["Name", "Phone", "Service", "Date", "Status"],
    };
  }
  const hits = bookings.filter((b) => {
    const str = [b[nameKey], b[phoneKey], b[serviceKey], b[addrKey]]
      .map((v) => String(v || "").toLowerCase())
      .join(" ");
    return q.split(/\s+/).some((w) => w.length > 1 && str.includes(w));
  });
  if (hits.length)
    return {
      text: `Search: "${query}"`,
      badge: `${hits.length}`,
      badgeColor: "#2563eb",
      rows: hits.slice(0, 12).map(row),
      headers: ["Name", "Phone", "Service", "Date", "Status"],
    };
  return null;
}

function answerTaskQuery(query, tasks) {
  const q = query.toLowerCase().trim();
  if (!tasks.length) return null;
  const F = getTaskFields(tasks);
  const { taskKey, techKey, statusKey, dateKey, addrKey } = F;
  const gs = (t) => String(t[statusKey] || "").toLowerCase();
  const pending = tasks.filter((t) => /pend|open/i.test(gs(t)));
  const done = tasks.filter((t) => /done|complet|finish/i.test(gs(t)));
  const inprog = tasks.filter((t) => /progress|working|active/i.test(gs(t)));
  const assigned = tasks.filter((t) => /assign/i.test(gs(t)));
  const todayList = tasks.filter((t) => isToday(t[dateKey]));
  const weekList = tasks.filter((t) => isThisWeek(t[dateKey]));
  const row = (t) => ({
    Task: fmt(t[taskKey]),
    Tech: fmt(t[techKey]),
    Status: fmt(t[statusKey]),
    Date: fmtDate(t[dateKey]),
    Area: fmt(t[addrKey]),
  });

  if (q.match(/graph|chart|compare|visual|pie|bar|trend|analyt/)) {
    const sMap = {},
      tMap = {},
      mMap = {};
    tasks.forEach((t) => {
      const s = fmt(t[statusKey]);
      sMap[s] = (sMap[s] || 0) + 1;
    });
    tasks.forEach((t) => {
      const s = fmt(t[techKey]);
      if (s !== "—") tMap[s] = (tMap[s] || 0) + 1;
    });
    tasks.forEach((t) => {
      try {
        const d = new Date(t[dateKey]);
        if (!isNaN(d)) {
          const k = d.toLocaleDateString("en-IN", {
            month: "short",
            year: "2-digit",
          });
          mMap[k] = (mMap[k] || 0) + 1;
        }
      } catch {}
    });
    return {
      type: "graphCompare",
      text: "Task Analytics",
      statusData: Object.entries(sMap).sort((a, b) => b[1] - a[1]),
      serviceData: Object.entries(tMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 7),
      monthData: Object.entries(mMap).slice(-8),
      total: tasks.length,
      serviceLabel: "Technician",
    };
  }
  if (q.match(/summary|total|overview|एकूण/)) {
    const tm = {};
    tasks.forEach((t) => {
      const tv = fmt(t[techKey]);
      if (tv !== "—") tm[tv] = (tm[tv] || 0) + 1;
    });
    const topTech =
      Object.entries(tm).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
    return {
      type: "summary",
      text: "Task Summary",
      stats: [
        {
          label: "Total",
          val: tasks.length,
          color: "#0891b2",
          bg: "#ecfeff",
          icon: "📋",
        },
        {
          label: "Pending",
          val: pending.length,
          color: "#d97706",
          bg: "#fffbeb",
          icon: "⏳",
        },
        {
          label: "Done",
          val: done.length,
          color: "#16a34a",
          bg: "#f0fdf4",
          icon: "✅",
        },
        {
          label: "In Progress",
          val: inprog.length,
          color: "#7c3aed",
          bg: "#f5f3ff",
          icon: "⚡",
        },
        {
          label: "Assigned",
          val: assigned.length,
          color: "#2563eb",
          bg: "#eff6ff",
          icon: "👷",
        },
        {
          label: "Today",
          val: todayList.length,
          color: "#db2777",
          bg: "#fdf2f8",
          icon: "📆",
        },
      ],
      extra: `Top Tech: ${topTech} · This Week: ${weekList.length}`,
    };
  }
  if (q.match(/today|आज/))
    return todayList.length
      ? {
          text: "Today's Tasks",
          badge: `${todayList.length}`,
          badgeColor: "#0891b2",
          rows: todayList.map(row),
          headers: ["Task", "Tech", "Status", "Date", "Area"],
        }
      : { text: "आज कोणतेही tasks नाहीत." };
  if (q.match(/week/))
    return {
      text: "This Week's Tasks",
      badge: `${weekList.length}`,
      badgeColor: "#7c3aed",
      rows: weekList.slice(0, 15).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  if (q.match(/pend|open/))
    return {
      text: "Pending Tasks",
      badge: `${pending.length}`,
      badgeColor: "#d97706",
      rows: pending.slice(0, 15).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  if (q.match(/done|complet|finish/))
    return {
      text: "Completed Tasks",
      badge: `${done.length}`,
      badgeColor: "#16a34a",
      rows: done.slice(0, 15).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  if (q.match(/progress|active|working/))
    return {
      text: "In Progress",
      badge: `${inprog.length}`,
      badgeColor: "#7c3aed",
      rows: inprog.slice(0, 15).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  if (q.match(/assign/))
    return {
      text: "Assigned Tasks",
      badge: `${assigned.length}`,
      badgeColor: "#2563eb",
      rows: assigned.slice(0, 15).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  if (q.match(/technician|tech|engineer|worker|staff/)) {
    const map = {};
    tasks.forEach((t) => {
      const tv = fmt(t[techKey]);
      if (tv !== "—") map[tv] = (map[tv] || 0) + 1;
    });
    const e = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return {
      text: "By Technician",
      rows: e.map(([t, c]) => ({ Technician: t, Tasks: c })),
      headers: ["Technician", "Tasks"],
      type: "chart",
      chartData: e.slice(0, 7),
    };
  }
  if (q.match(/location|city|area|address/)) {
    const map = {};
    tasks.forEach((t) => {
      const a = fmt(t[addrKey]);
      if (a !== "—") map[a] = (map[a] || 0) + 1;
    });
    return {
      text: "Task Locations",
      rows: Object.entries(map)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([l, c]) => ({ Location: l, Count: c })),
      headers: ["Location", "Count"],
    };
  }
  if (q.match(/recent|latest|new/)) {
    const sorted = [...tasks].sort(
      (a, b) => new Date(b[dateKey]) - new Date(a[dateKey]),
    );
    return {
      text: "Recent Tasks",
      badge: "10",
      badgeColor: "#0891b2",
      rows: sorted.slice(0, 10).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  }
  const hits = tasks.filter((t) => {
    const str = [t[taskKey], t[techKey], t[statusKey], t[addrKey]]
      .map((v) => String(v || "").toLowerCase())
      .join(" ");
    return q.split(/\s+/).some((w) => w.length > 1 && str.includes(w));
  });
  if (hits.length)
    return {
      text: `Search: "${query}"`,
      badge: `${hits.length}`,
      badgeColor: "#0891b2",
      rows: hits.slice(0, 12).map(row),
      headers: ["Task", "Tech", "Status", "Date", "Area"],
    };
  return null;
}

function answerAdQuery(query, ads) {
  const q = query.toLowerCase().trim();
  if (!ads.length) return null;
  const F = getAdFields(ads);
  const {
    titleKey,
    descKey,
    statusKey,
    dateKey,
    typeKey,
    targetKey,
    budgetKey,
    imageKey,
  } = F;
  const gs = (a) => String(a[statusKey] || "").toLowerCase();
  const active = ads.filter((a) => /active|publish|live|running/i.test(gs(a)));
  const inactive = ads.filter((a) => /inactive|pause|stop/i.test(gs(a)));
  const expired = ads.filter((a) => /expir|end|close/i.test(gs(a)));
  const draft = ads.filter((a) => /draft|pending|review/i.test(gs(a)));
  const todayList = ads.filter((a) => isToday(a[dateKey]));
  const weekList = ads.filter((a) => isThisWeek(a[dateKey]));

  const row = (a) => {
    const r = {
      Title: fmt(a[titleKey]),
      Status: fmt(a[statusKey]),
      Type: fmt(a[typeKey]),
      Date: fmtDate(a[dateKey]),
    };
    if (a[budgetKey] != null) r.Budget = fmt(a[budgetKey]);
    if (a[targetKey] != null) r.Target = fmt(a[targetKey]);
    return r;
  };
  const rowHeaders = (a) => {
    const h = ["Title", "Status", "Type", "Date"];
    if (a[0] && a[0][budgetKey] != null) h.push("Budget");
    if (a[0] && a[0][targetKey] != null) h.push("Target");
    return h;
  };

  if (q.match(/graph|chart|compare|visual|pie|bar|trend|analyt/)) {
    const sMap = {},
      tMap = {},
      mMap = {};
    ads.forEach((a) => {
      const s = fmt(a[statusKey]);
      sMap[s] = (sMap[s] || 0) + 1;
    });
    ads.forEach((a) => {
      const t = fmt(a[typeKey]);
      if (t !== "—") tMap[t] = (tMap[t] || 0) + 1;
    });
    ads.forEach((a) => {
      try {
        const d = new Date(a[dateKey]);
        if (!isNaN(d)) {
          const k = d.toLocaleDateString("en-IN", {
            month: "short",
            year: "2-digit",
          });
          mMap[k] = (mMap[k] || 0) + 1;
        }
      } catch {}
    });
    return {
      type: "graphCompare",
      text: "Advertisement Analytics",
      statusData: Object.entries(sMap).sort((a, b) => b[1] - a[1]),
      serviceData: Object.entries(tMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 7),
      monthData: Object.entries(mMap).slice(-8),
      total: ads.length,
      serviceLabel: "Ad Type",
    };
  }
  if (q.match(/summary|total|overview|all|एकूण/)) {
    const tm = {};
    ads.forEach((a) => {
      const t = fmt(a[typeKey]);
      if (t !== "—") tm[t] = (tm[t] || 0) + 1;
    });
    const topType =
      Object.entries(tm).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
    const totalBudget = ads.reduce((sum, a) => {
      const b = parseFloat(String(a[budgetKey] || "").replace(/[^0-9.]/g, ""));
      return sum + (isNaN(b) ? 0 : b);
    }, 0);
    return {
      type: "summary",
      text: "Advertisement Summary",
      stats: [
        {
          label: "Total Ads",
          val: ads.length,
          color: "#db2777",
          bg: "#fdf2f8",
          icon: "📢",
        },
        {
          label: "Active",
          val: active.length,
          color: "#16a34a",
          bg: "#f0fdf4",
          icon: "🟢",
        },
        {
          label: "Inactive",
          val: inactive.length,
          color: "#64748b",
          bg: "#f1f5f9",
          icon: "⏸️",
        },
        {
          label: "Expired",
          val: expired.length,
          color: "#dc2626",
          bg: "#fef2f2",
          icon: "⛔",
        },
        {
          label: "Draft",
          val: draft.length,
          color: "#d97706",
          bg: "#fffbeb",
          icon: "📝",
        },
        {
          label: "This Week",
          val: weekList.length,
          color: "#7c3aed",
          bg: "#f5f3ff",
          icon: "📆",
        },
      ],
      extra: `Top Type: ${topType}${totalBudget > 0 ? " · Total Budget: ₹" + totalBudget.toLocaleString("en-IN") : ""}`,
    };
  }
  if (q.match(/active|live|running|publish/)) {
    return {
      text: "Active Advertisements",
      badge: `${active.length}`,
      badgeColor: "#16a34a",
      rows: active.slice(0, 15).map(row),
      headers: rowHeaders(active),
    };
  }
  if (q.match(/inactive|pause|stopped/)) {
    return {
      text: "Inactive Ads",
      badge: `${inactive.length}`,
      badgeColor: "#64748b",
      rows: inactive.slice(0, 15).map(row),
      headers: rowHeaders(inactive),
    };
  }
  if (q.match(/expir|ended/)) {
    return {
      text: "Expired Ads",
      badge: `${expired.length}`,
      badgeColor: "#dc2626",
      rows: expired.slice(0, 15).map(row),
      headers: rowHeaders(expired),
    };
  }
  if (q.match(/draft|pending|review/)) {
    return {
      text: "Draft Ads",
      badge: `${draft.length}`,
      badgeColor: "#d97706",
      rows: draft.slice(0, 15).map(row),
      headers: rowHeaders(draft),
    };
  }
  if (q.match(/today|आज/))
    return todayList.length
      ? {
          text: "Today's Ads",
          badge: `${todayList.length}`,
          badgeColor: "#db2777",
          rows: todayList.map(row),
          headers: rowHeaders(todayList),
        }
      : { text: "आज कोणतेही नवीन ads नाहीत." };
  if (q.match(/week/))
    return {
      text: "This Week's Ads",
      badge: `${weekList.length}`,
      badgeColor: "#7c3aed",
      rows: weekList.slice(0, 15).map(row),
      headers: rowHeaders(weekList),
    };
  if (q.match(/type|category|platform|medium|channel/)) {
    const map = {};
    ads.forEach((a) => {
      const t = fmt(a[typeKey]);
      if (t !== "—") map[t] = (map[t] || 0) + 1;
    });
    const e = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return {
      text: "Ads by Type",
      rows: e.map(([t, c]) => ({ Type: t, Count: c })),
      headers: ["Type", "Count"],
      type: "chart",
      chartData: e.slice(0, 7),
    };
  }
  if (q.match(/budget|cost|spend|price|amount|money|₹/)) {
    const withBudget = ads
      .filter((a) => a[budgetKey] != null && a[budgetKey] !== "")
      .map((a) => ({
        Title: fmt(a[titleKey]),
        Type: fmt(a[typeKey]),
        Budget: fmt(a[budgetKey]),
        Status: fmt(a[statusKey]),
      }))
      .sort((a, b) => {
        const av = parseFloat(String(a.Budget).replace(/[^0-9.]/g, "")),
          bv = parseFloat(String(b.Budget).replace(/[^0-9.]/g, ""));
        return (isNaN(bv) ? 0 : bv) - (isNaN(av) ? 0 : av);
      });
    if (!withBudget.length) return { text: "Budget data उपलब्ध नाही." };
    return {
      text: "Ads by Budget",
      badge: `${withBudget.length}`,
      badgeColor: "#db2777",
      rows: withBudget.slice(0, 15),
      headers: ["Title", "Type", "Budget", "Status"],
    };
  }
  if (q.match(/target|audience|location|city|area/)) {
    const map = {};
    ads.forEach((a) => {
      const t = fmt(a[targetKey]);
      if (t !== "—") map[t] = (map[t] || 0) + 1;
    });
    if (!Object.keys(map).length) return { text: "Target data उपलब्ध नाही." };
    return {
      text: "Target Audience Breakdown",
      rows: Object.entries(map)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([l, c]) => ({ Target: l, Count: c })),
      headers: ["Target", "Count"],
    };
  }
  if (q.match(/recent|latest|new/)) {
    const sorted = [...ads].sort(
      (a, b) => new Date(b[dateKey]) - new Date(a[dateKey]),
    );
    return {
      text: "Recent Ads",
      badge: "10",
      badgeColor: "#db2777",
      rows: sorted.slice(0, 10).map(row),
      headers: rowHeaders(sorted),
    };
  }
  if (q.match(/status/)) {
    const map = {};
    ads.forEach((a) => {
      const s = fmt(a[statusKey]);
      map[s] = (map[s] || 0) + 1;
    });
    return {
      text: "Status Overview",
      rows: Object.entries(map)
        .sort((a, b) => b[1] - a[1])
        .map(([s, c]) => ({ Status: s, Count: c })),
      headers: ["Status", "Count"],
    };
  }
  // Image/media ads
  if (q.match(/image|photo|banner|media|visual/)) {
    const withImg = ads.filter(
      (a) => a[imageKey] && String(a[imageKey]).match(/^https?:\/\//),
    );
    if (withImg.length)
      return {
        type: "adCards",
        text: "Advertisement Gallery",
        ads: withImg.slice(0, 12),
        F,
      };
    return { text: "Image data उपलब्ध नाही." };
  }
  // Search
  const hits = ads.filter((a) => {
    const str = [
      a[titleKey],
      a[descKey],
      a[typeKey],
      a[targetKey],
      a[statusKey],
    ]
      .map((v) => String(v || "").toLowerCase())
      .join(" ");
    return q.split(/\s+/).some((w) => w.length > 1 && str.includes(w));
  });
  if (hits.length)
    return {
      text: `Search: "${query}"`,
      badge: `${hits.length}`,
      badgeColor: "#db2777",
      rows: hits.slice(0, 12).map(row),
      headers: rowHeaders(hits),
    };
  return null;
}

// ── CHART COMPONENTS ──
function MiniBarChart({ data }) {
  const max = Math.max(...data.map((d) => d[1]), 1);
  return (
    <div
      style={{
        marginTop: 14,
        display: "flex",
        flexDirection: "column",
        gap: 7,
      }}
    >
      {data.map(([label, val], i) => (
        <div
          key={label}
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <span
            style={{
              width: 96,
              fontSize: "0.72rem",
              color: "#64748b",
              textAlign: "right",
              flexShrink: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          <div
            style={{
              flex: 1,
              height: 10,
              background: "#f1f5f9",
              borderRadius: 99,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${(val / max) * 100}%`,
                background: CHART_COLORS[i % CHART_COLORS.length],
                borderRadius: 99,
                transition: "width 0.9s cubic-bezier(.4,0,.2,1)",
              }}
            />
          </div>
          <span
            style={{
              width: 28,
              fontSize: "0.72rem",
              fontWeight: 700,
              color: CHART_COLORS[i % CHART_COLORS.length],
              flexShrink: 0,
            }}
          >
            {val}
          </span>
        </div>
      ))}
    </div>
  );
}

function HorizontalBarChart({ data, colorFn, title, total }) {
  const max = Math.max(...data.map((d) => d[1]), 1);
  return (
    <div>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: "#475569",
          marginBottom: 10,
          letterSpacing: 0.4,
        }}
      >
        {title.toUpperCase()}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {data.map(([label, val], i) => {
          const pct = Math.round((val / total) * 100),
            color = colorFn(label, i);
          return (
            <div key={label}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: "0.72rem",
                    color: "#374151",
                    fontWeight: 600,
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
                    {pct}%
                  </span>
                  <span style={{ fontSize: "0.72rem", fontWeight: 800, color }}>
                    {val}
                  </span>
                </div>
              </div>
              <div
                style={{
                  height: 10,
                  background: "#f1f5f9",
                  borderRadius: 99,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(val / max) * 100}%`,
                    background: color,
                    borderRadius: 99,
                    transition: "width 0.9s cubic-bezier(.4,0,.2,1)",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        {data.map(([label, val], i) => (
          <div
            key={label}
            style={{ display: "flex", alignItems: "center", gap: 5 }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: colorFn(label, i),
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "0.65rem", color: "#64748b" }}>
              {label}: <b style={{ color: "#374151" }}>{val}</b>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendChart({ data, title }) {
  if (!data.length)
    return (
      <div
        style={{
          color: "#94a3b8",
          fontSize: "0.75rem",
          textAlign: "center",
          padding: 20,
        }}
      >
        No trend data
      </div>
    );
  const max = Math.max(...data.map((d) => d[1]), 1),
    W = 320,
    H = 120,
    PAD = 28;
  const stepX = (W - PAD * 2) / Math.max(data.length - 1, 1);
  const pts = data.map(([, v], i) => [
    PAD + i * stepX,
    H - PAD - (v / max) * (H - PAD * 2),
  ]);
  const pD = pts
    .map(
      (p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`,
    )
    .join(" ");
  const aD =
    pD +
    ` L${pts[pts.length - 1][0].toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;
  return (
    <div>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: "#475569",
          marginBottom: 10,
          letterSpacing: 0.4,
        }}
      >
        {title.toUpperCase()}
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          style={{ display: "block", minWidth: W }}
        >
          <defs>
            <linearGradient id="tG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563eb" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#2563eb" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={f}
              x1={PAD}
              y1={H - PAD - f * (H - PAD * 2)}
              x2={W - PAD}
              y2={H - PAD - f * (H - PAD * 2)}
              stroke="#f1f5f9"
              strokeWidth={1}
            />
          ))}
          <path d={aD} fill="url(#tG)" />
          <path
            d={pD}
            fill="none"
            stroke="#2563eb"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {pts.map(([x, y], i) => (
            <g key={i}>
              <circle
                cx={x}
                cy={y}
                r={4}
                fill="#fff"
                stroke="#2563eb"
                strokeWidth={2}
              />
              <text
                x={x}
                y={H - 8}
                textAnchor="middle"
                fontSize="8"
                fill="#94a3b8"
              >
                {data[i][0]}
              </text>
              <text
                x={x}
                y={y - 8}
                textAnchor="middle"
                fontSize="9"
                fill="#2563eb"
                fontWeight="700"
              >
                {data[i][1]}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function DonutChart({ data, title, total }) {
  if (!data.length) return null;
  const R = 54,
    CX = 80,
    CY = 72,
    sW = 18,
    circ = 2 * Math.PI * R;
  let off = 0;
  const segs = data.map(([label, val], i) => {
    const color =
      STATUS_COLORS[label.toLowerCase().replace(/\s/g, "")] ||
      CHART_COLORS[i % CHART_COLORS.length];
    const dash = (val / total) * circ;
    const seg = { label, val, color, dash, off };
    off += dash;
    return seg;
  });
  return (
    <div>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: "#475569",
          marginBottom: 10,
          letterSpacing: 0.4,
        }}
      >
        {title.toUpperCase()}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <svg width={160} height={144} viewBox="0 0 160 144">
          {segs.map((seg, i) => (
            <circle
              key={i}
              cx={CX}
              cy={CY}
              r={R}
              fill="none"
              stroke={seg.color}
              strokeWidth={sW}
              strokeDasharray={`${seg.dash} ${circ - seg.dash}`}
              strokeDashoffset={-seg.off + circ * 0.25}
            />
          ))}
          <text
            x={CX}
            y={CY - 6}
            textAnchor="middle"
            fontSize="20"
            fontWeight="800"
            fill="#1e293b"
          >
            {total}
          </text>
          <text
            x={CX}
            y={CY + 12}
            textAnchor="middle"
            fontSize="9"
            fill="#94a3b8"
          >
            TOTAL
          </text>
        </svg>
        <div
          style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}
        >
          {segs.map((seg) => (
            <div
              key={seg.label}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: seg.color,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "#374151",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {seg.label}
              </span>
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: seg.color,
                }}
              >
                {seg.val}
              </span>
              <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>
                ({Math.round((seg.val / total) * 100)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GraphCompare({ msg }) {
  const [activeTab, setActiveTab] = useState("status");
  const {
    statusData = [],
    serviceData = [],
    monthData = [],
    total,
    serviceLabel = "Service",
  } = msg;
  const tabs = [
    { key: "status", label: "Status", icon: "🔵" },
    {
      key: "service",
      label: serviceLabel,
      icon:
        serviceLabel === "Technician"
          ? "👷"
          : serviceLabel === "Ad Type"
            ? "📢"
            : "🔧",
    },
    { key: "trend", label: "Trend", icon: "📈" },
    { key: "donut", label: "Donut", icon: "🍩" },
  ];
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 14,
          background: "#f1f5f9",
          borderRadius: 12,
          padding: 4,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              flex: 1,
              padding: "6px 4px",
              borderRadius: 9,
              border: "none",
              cursor: "pointer",
              fontFamily: "'Outfit',sans-serif",
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: 0.3,
              transition: "all 0.18s",
              background: activeTab === t.key ? "#ffffff" : "transparent",
              color: activeTab === t.key ? "#2563eb" : "#94a3b8",
              boxShadow:
                activeTab === t.key ? "0 1px 6px rgba(0,0,0,0.1)" : "none",
            }}
          >
            <span style={{ marginRight: 3 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === "status" && (
        <HorizontalBarChart
          data={statusData}
          colorFn={(l) =>
            STATUS_COLORS[l.toLowerCase().replace(/\s/g, "")] || "#64748b"
          }
          title="By Status"
          total={total}
        />
      )}
      {activeTab === "service" && (
        <HorizontalBarChart
          data={serviceData}
          colorFn={(_, i) => CHART_COLORS[i % CHART_COLORS.length]}
          title={`By ${serviceLabel}`}
          total={total}
        />
      )}
      {activeTab === "trend" && (
        <TrendChart data={monthData} title="Monthly Trend" />
      )}
      {activeTab === "donut" && (
        <DonutChart
          data={statusData}
          title="Status Distribution"
          total={total}
        />
      )}
    </div>
  );
}

function SummaryCards({ stats, extra }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 8,
        }}
      >
        {stats.map(({ label, val, color, bg, icon }) => (
          <div
            key={label}
            style={{
              background: bg,
              border: `1px solid ${color}22`,
              borderRadius: 12,
              padding: "10px 8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "1rem", marginBottom: 2 }}>{icon}</div>
            <div
              style={{
                fontSize: "1.4rem",
                fontWeight: 800,
                color,
                lineHeight: 1,
              }}
            >
              {val}
            </div>
            <div
              style={{
                fontSize: "0.62rem",
                color: "#94a3b8",
                marginTop: 2,
                fontWeight: 600,
                letterSpacing: 0.3,
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </div>
      {extra && (
        <div
          style={{
            marginTop: 10,
            fontSize: "0.72rem",
            color: "#94a3b8",
            background: "#f8fafc",
            borderRadius: 8,
            padding: "6px 12px",
            textAlign: "center",
            border: "1px solid #e2e8f0",
          }}
        >
          {extra}
        </div>
      )}
    </div>
  );
}

// Ad Cards view - for image-rich advertisements
function AdCards({ ads, F }) {
  const { titleKey, descKey, statusKey, typeKey, imageKey, budgetKey } = F;
  return (
    <div
      style={{
        marginTop: 12,
        display: "grid",
        gridTemplateColumns: "repeat(2,1fr)",
        gap: 10,
      }}
    >
      {ads.map((ad, i) => {
        const sm = getStatusMeta(fmt(ad[statusKey]));
        return (
          <div
            key={i}
            style={{
              borderRadius: 12,
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            {ad[imageKey] && String(ad[imageKey]).match(/^https?:\/\//) && (
              <img
                src={ad[imageKey]}
                alt={fmt(ad[titleKey])}
                style={{ width: "100%", height: 90, objectFit: "cover" }}
                onError={(e) => (e.target.style.display = "none")}
              />
            )}
            <div style={{ padding: "8px 10px" }}>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#1e293b",
                  marginBottom: 4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fmt(ad[titleKey])}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 8px",
                    borderRadius: 99,
                    background: sm.bg,
                    color: sm.color,
                    fontSize: "0.62rem",
                    fontWeight: 700,
                  }}
                >
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: sm.dot,
                    }}
                  />
                  {fmt(ad[statusKey])}
                </span>
                {ad[typeKey] && (
                  <span style={{ fontSize: "0.62rem", color: "#94a3b8" }}>
                    {fmt(ad[typeKey])}
                  </span>
                )}
                {ad[budgetKey] && (
                  <span
                    style={{
                      fontSize: "0.62rem",
                      color: "#16a34a",
                      fontWeight: 700,
                    }}
                  >
                    ₹{fmt(ad[budgetKey])}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DataTable({ rows, headers }) {
  const [filter, setFilter] = useState("");
  const filtered = rows.filter((r) =>
    Object.values(r).some((v) =>
      String(v).toLowerCase().includes(filter.toLowerCase()),
    ),
  );
  return (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "5px 10px",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="#94a3b8" strokeWidth="2" />
            <path
              d="M20 20l-3-3"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#374151",
              fontSize: "0.73rem",
              fontFamily: "'Outfit',sans-serif",
            }}
          />
          {filter && (
            <button
              onClick={() => setFilter("")}
              style={{
                background: "none",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                padding: 0,
                fontSize: 11,
              }}
            >
              ✕
            </button>
          )}
        </div>
        <span
          style={{
            fontSize: "0.67rem",
            color: "#94a3b8",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {filtered.length} rows
        </span>
      </div>
      <div
        style={{
          overflowX: "auto",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.75rem",
          }}
        >
          <thead>
            <tr
              style={{ background: "linear-gradient(90deg,#f8fafc,#f1f5f9)" }}
            >
              {headers.map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    color: "#475569",
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    whiteSpace: "nowrap",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.68rem",
                    fontFamily: "'Outfit',sans-serif",
                  }}
                >
                  {h.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  style={{
                    textAlign: "center",
                    padding: "18px",
                    color: "#94a3b8",
                  }}
                >
                  No records found
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    background: i % 2 === 0 ? "#ffffff" : "#fafbfd",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#eff6ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      i % 2 === 0 ? "#ffffff" : "#fafbfd")
                  }
                >
                  {Object.values(row).map((v, j) => {
                    const sv = String(v),
                      sm = getStatusMeta(sv);
                    const isS =
                      sv !== "—" &&
                      (STATUS_META[sv.toLowerCase()] ||
                        STATUS_META[sv.toLowerCase().replace(/\s/g, "")]);
                    return (
                      <td
                        key={j}
                        style={{
                          padding: "7px 12px",
                          borderBottom: "1px solid #f1f5f9",
                          color: "#374151",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isS ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "3px 9px",
                              borderRadius: 99,
                              background: sm.bg,
                              color: sm.color,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                            }}
                          >
                            <span
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                background: sm.dot,
                                flexShrink: 0,
                              }}
                            />
                            {sv}
                          </span>
                        ) : (
                          <span style={{ fontFamily: "'Outfit',sans-serif" }}>
                            {sv}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Source badge color map
const SOURCE_META = {
  bookings: {
    bg: "#eff6ff",
    color: "#2563eb",
    border: "#bfdbfe",
    label: "📅 BOOKINGS",
  },
  tasks: {
    bg: "#ecfeff",
    color: "#0891b2",
    border: "#a5f3fc",
    label: "🔧 TASKS",
  },
  ads: { bg: "#fdf2f8", color: "#db2777", border: "#fbcfe8", label: "📢 ADS" },
};

function BotBubble({ msg }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 40);
    return () => clearTimeout(t);
  }, []);
  const hasContent =
    msg.type === "summary" ||
    msg.type === "graphCompare" ||
    msg.type === "adCards" ||
    msg.rows ||
    msg.type === "chart";
  const sm = msg.source ? SOURCE_META[msg.source] : null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(10px)",
        transition: "all 0.28s ease",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: "0 2px 10px rgba(37,99,235,0.25)",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="4" fill="white" />
          <circle cx="12" cy="12" r="1.5" fill="#2563eb" />
          <path
            d="M12 4v2M12 18v2M4 12h2M18 12h2"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.7"
          />
        </svg>
      </div>
      <div
        style={{
          maxWidth: "86%",
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "4px 16px 16px 16px",
          padding: "12px 16px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: hasContent ? 4 : 0,
          }}
        >
          <span
            style={{ fontSize: "0.84rem", fontWeight: 700, color: "#1e293b" }}
          >
            {msg.text}
          </span>
          {msg.badge && (
            <span
              style={{
                padding: "2px 9px",
                borderRadius: 99,
                background: msg.badgeColor + "18",
                color: msg.badgeColor,
                fontSize: "0.68rem",
                fontWeight: 700,
                border: `1px solid ${msg.badgeColor}33`,
              }}
            >
              {msg.badge}
            </span>
          )}
          {sm && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 99,
                fontSize: "0.62rem",
                fontWeight: 700,
                background: sm.bg,
                color: sm.color,
                border: `1px solid ${sm.border}`,
              }}
            >
              {sm.label}
            </span>
          )}
          {msg.type === "graphCompare" && (
            <span
              style={{
                padding: "2px 9px",
                borderRadius: 99,
                background: "#eff6ff",
                color: "#2563eb",
                fontSize: "0.68rem",
                fontWeight: 700,
                border: "1px solid #bfdbfe",
              }}
            >
              📊 4 views
            </span>
          )}
        </div>
        {msg.extra && !hasContent && (
          <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 4 }}>
            {msg.extra}
          </div>
        )}
        {msg.type === "summary" && msg.stats && (
          <SummaryCards stats={msg.stats} extra={msg.extra} />
        )}
        {msg.type === "graphCompare" && <GraphCompare msg={msg} />}
        {msg.type === "adCards" && <AdCards ads={msg.ads} F={msg.F} />}
        {msg.rows?.length > 0 && (
          <DataTable rows={msg.rows} headers={msg.headers} />
        )}
        {msg.type === "chart" && msg.chartData && (
          <MiniBarChart data={msg.chartData} />
        )}
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 30);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "flex-end",
        gap: 8,
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(8px)",
        transition: "all 0.22s ease",
      }}
    >
      <div
        style={{
          maxWidth: "70%",
          background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
          borderRadius: "16px 4px 16px 16px",
          padding: "10px 16px",
          boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
        }}
      >
        <span
          style={{ fontSize: "0.82rem", color: "#ffffff", fontWeight: 500 }}
        >
          {text}
        </span>
      </div>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
          border: "1px solid #bfdbfe",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="4" stroke="#2563eb" strokeWidth="2" />
          <path
            d="M4 20c0-4 3.6-7 8-7s8 3 8 7"
            stroke="#2563eb"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="4" fill="white" />
          <circle cx="12" cy="12" r="1.5" fill="#2563eb" />
        </svg>
      </div>
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "4px 16px 16px 16px",
          padding: "12px 18px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#2563eb",
                animation: "bob 1.2s infinite",
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const TABS = [
  {
    key: "bookings",
    label: "Appointments",
    icon: "📅",
    color: "#2563eb",
    grad: "#1d4ed8",
  },
  {
    key: "tasks",
    label: "Assign Tasks",
    icon: "🔧",
    color: "#0891b2",
    grad: "#0e7490",
  },
  {
    key: "ads",
    label: "Advertisements",
    icon: "📢",
    color: "#db2777",
    grad: "#be185d",
  },
];
const SUGG = {
  bookings: [
    "📊 Graph",
    "Summary",
    "Today",
    "Pending",
    "Confirmed",
    "Cancelled",
    "Service",
    "Location",
    "Recent",
    "This Week",
  ],
  tasks: [
    "📊 Graph",
    "Summary",
    "Today",
    "Pending",
    "Done",
    "In Progress",
    "Assigned",
    "Technician",
    "Location",
    "Recent",
  ],
  ads: [
    "📊 Graph",
    "Summary",
    "Active",
    "Inactive",
    "Expired",
    "Draft",
    "Today",
    "Type",
    "Budget",
    "Recent",
  ],
};

export default function Ai() {
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState({ b: true, t: true, a: true });
  const [mode, setMode] = useState("bookings");
  const [messages, setMessages] = useState([
    {
      id: 1,
      from: "bot",
      text: "👋 नमस्ते! मी CCTV Partner Triple AI आहे. तीनही APIs load होत आहेत…",
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetch(API_BOOKINGS)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : data.data || data.result || data.bookings || [];
        setBookings(list);
        const F = getBookingFields(list);
        const c = list.filter((b) =>
          /confirm/i.test(String(b[F.statusKey] || "")),
        ).length;
        const p = list.filter((b) =>
          /pend/i.test(String(b[F.statusKey] || "")),
        ).length;
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            from: "bot",
            text: `📅 Appointments load झाले! ${list.length} records`,
            badge: `${list.length}`,
            badgeColor: "#2563eb",
            source: "bookings",
            extra: `Confirmed: ${c} · Pending: ${p}`,
          },
        ]);
      })
      .catch((e) =>
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            from: "bot",
            text: `❌ Bookings error: ${e.message}`,
          },
        ]),
      )
      .finally(() => setLoading((l) => ({ ...l, b: false })));
  }, []);

  useEffect(() => {
    fetch(API_TASKS)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : data.data || data.result || data.tasks || [];
        setTasks(list);
        const F = getTaskFields(list);
        const p = list.filter((t) =>
          /pend|open/i.test(String(t[F.statusKey] || "")),
        ).length;
        const d = list.filter((t) =>
          /done|complet/i.test(String(t[F.statusKey] || "")),
        ).length;
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            from: "bot",
            text: `🔧 Tasks load झाले! ${list.length} records`,
            badge: `${list.length}`,
            badgeColor: "#0891b2",
            source: "tasks",
            extra: `Pending: ${p} · Done: ${d}`,
          },
        ]);
      })
      .catch((e) =>
        setMessages((prev) => [
          ...prev,
          { id: Date.now(), from: "bot", text: `❌ Tasks error: ${e.message}` },
        ]),
      )
      .finally(() => setLoading((l) => ({ ...l, t: false })));
  }, []);

  useEffect(() => {
    fetch(API_ADS)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : data.data || data.result || data.advertisements || data.ads || [];
        setAds(list);
        const F = getAdFields(list);
        const active = list.filter((a) =>
          /active|publish|live/i.test(String(a[F.statusKey] || "")),
        ).length;
        const total = list.length;
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            from: "bot",
            text: `📢 Advertisements load झाले! ${total} records`,
            badge: `${total}`,
            badgeColor: "#db2777",
            source: "ads",
            extra: `Active: ${active} · Total: ${total}`,
          },
        ]);
      })
      .catch((e) =>
        setMessages((prev) => [
          ...prev,
          { id: Date.now(), from: "bot", text: `❌ Ads error: ${e.message}` },
        ]),
      )
      .finally(() => setLoading((l) => ({ ...l, a: false })));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const send = async (text) => {
    const q = (text || input).trim().replace(/^📊 /, "");
    if (!q) return;
    setInput("");
    setMessages((p) => [...p, { id: Date.now(), from: "user", text: q }]);
    setTyping(true);
    await new Promise((r) => setTimeout(r, 380));
    let ans = null;
    if (mode === "bookings") ans = answerBookingQuery(q, bookings);
    else if (mode === "tasks") ans = answerTaskQuery(q, tasks);
    else ans = answerAdQuery(q, ads);
    if (ans) ans.source = mode;
    if (!ans) {
      const hints = {
        bookings: "Graph / Summary / Pending / Service",
        tasks: "Graph / Summary / Technician / Pending",
        ads: "Graph / Summary / Active / Budget / Type",
      };
      ans = { text: `"${q}" समजले नाही. Try: ${hints[mode]}` };
    }
    setMessages((p) => [...p, { id: Date.now() + 1, from: "bot", ...ans }]);
    setTyping(false);
  };

  const isLoading = loading.b || loading.t || loading.a;
  const curTab = TABS.find((t) => t.key === mode);
  const ac = curTab.color;

  // Header stats per mode
  const BF = getBookingFields(bookings),
    TF = getTaskFields(tasks),
    AF = getAdFields(ads);
  const headerStats =
    mode === "bookings"
      ? [
          {
            label: "Total",
            val: bookings.length,
            color: "#2563eb",
            bg: "#eff6ff",
          },
          {
            label: "Confirmed",
            val: bookings.filter((b) =>
              /confirm/i.test(String(b[BF.statusKey] || "")),
            ).length,
            color: "#16a34a",
            bg: "#f0fdf4",
          },
          {
            label: "Pending",
            val: bookings.filter((b) =>
              /pend/i.test(String(b[BF.statusKey] || "")),
            ).length,
            color: "#d97706",
            bg: "#fffbeb",
          },
          {
            label: "Today",
            val: bookings.filter((b) => isToday(b[BF.dateKey])).length,
            color: "#7c3aed",
            bg: "#f5f3ff",
          },
        ]
      : mode === "tasks"
        ? [
            {
              label: "Total",
              val: tasks.length,
              color: "#0891b2",
              bg: "#ecfeff",
            },
            {
              label: "Pending",
              val: tasks.filter((t) =>
                /pend|open/i.test(String(t[TF.statusKey] || "")),
              ).length,
              color: "#d97706",
              bg: "#fffbeb",
            },
            {
              label: "Done",
              val: tasks.filter((t) =>
                /done|complet/i.test(String(t[TF.statusKey] || "")),
              ).length,
              color: "#16a34a",
              bg: "#f0fdf4",
            },
            {
              label: "Today",
              val: tasks.filter((t) => isToday(t[TF.dateKey])).length,
              color: "#7c3aed",
              bg: "#f5f3ff",
            },
          ]
        : [
            {
              label: "Total Ads",
              val: ads.length,
              color: "#db2777",
              bg: "#fdf2f8",
            },
            {
              label: "Active",
              val: ads.filter((a) =>
                /active|publish|live/i.test(String(a[AF.statusKey] || "")),
              ).length,
              color: "#16a34a",
              bg: "#f0fdf4",
            },
            {
              label: "Expired",
              val: ads.filter((a) =>
                /expir/i.test(String(a[AF.statusKey] || "")),
              ).length,
              color: "#dc2626",
              bg: "#fef2f2",
            },
            {
              label: "Draft",
              val: ads.filter((a) =>
                /draft|pending/i.test(String(a[AF.statusKey] || "")),
              ).length,
              color: "#d97706",
              bg: "#fffbeb",
            },
          ];

  const suggestions = SUGG[mode];
  const counts = {
    bookings: bookings.length,
    tasks: tasks.length,
    ads: ads.length,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "90vh",
        overflow: "hidden",
        background: "#f8fafc",
        fontFamily: "'Outfit','Segoe UI',sans-serif",
        maxWidth: 1440,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          position: "fixed",
          top: -80,
          right: -80,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: `radial-gradient(circle,${ac}12,transparent 70%)`,
          pointerEvents: "none",
          transition: "background 0.4s",
        }}
      />

      {/* ── HEADER ── */}
      <div
        style={{
          flexShrink: 0,
          background: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          padding: "12px 20px 0",
          boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 13,
                background: `linear-gradient(135deg,${ac},${curTab.grad})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 14px ${ac}50`,
                flexShrink: 0,
                transition: "background 0.3s",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="5"
                  stroke="white"
                  strokeWidth="1.8"
                />
                <circle cx="12" cy="12" r="2" fill="white" />
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="white"
                  strokeWidth="0.8"
                  strokeDasharray="2 2.5"
                  opacity="0.4"
                />
                <path
                  d="M12 3v2M12 19v2M3 12h2M19 12h2"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div>
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1.1,
                }}
              >
                CCTV{" "}
                <span style={{ color: ac, transition: "color 0.3s" }}>
                  Partner
                </span>{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    color: "#94a3b8",
                  }}
                >
                  Triple AI
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 3,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: isLoading ? "#f59e0b" : "#22c55e",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontSize: "0.63rem",
                    color: "#94a3b8",
                    fontWeight: 500,
                  }}
                >
                  {isLoading
                    ? "Loading APIs…"
                    : `📅 ${bookings.length} · 🔧 ${tasks.length} · 📢 ${ads.length}`}
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {headerStats.map(({ label, val, color, bg }) => (
              <div
                key={label}
                style={{
                  textAlign: "center",
                  padding: "5px 10px",
                  borderRadius: 10,
                  background: bg,
                  border: `1px solid ${color}22`,
                  minWidth: 52,
                  transition: "all 0.3s",
                }}
              >
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: 800,
                    color,
                    lineHeight: 1,
                  }}
                >
                  {val}
                </div>
                <div
                  style={{
                    fontSize: "0.54rem",
                    color: "#94a3b8",
                    marginTop: 2,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                  }}
                >
                  {label.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MODE TABS ── */}
        <div
          style={{ display: "flex", gap: 0, borderTop: "1px solid #f1f5f9" }}
        >
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setMode(tab.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                border: "none",
                cursor: "pointer",
                fontFamily: "'Outfit',sans-serif",
                fontSize: "0.72rem",
                fontWeight: 700,
                transition: "all 0.18s",
                background: "transparent",
                color: mode === tab.key ? tab.color : "#94a3b8",
                borderBottom:
                  mode === tab.key
                    ? `2px solid ${tab.color}`
                    : "2px solid transparent",
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
              <span
                style={{
                  padding: "1px 7px",
                  borderRadius: 99,
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  background: mode === tab.key ? tab.color + "18" : "#f1f5f9",
                  color: mode === tab.key ? tab.color : "#94a3b8",
                  transition: "all 0.18s",
                }}
              >
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── MESSAGES ── */}
      <div
        style={{
          flex: "1 1 0",
          minHeight: 0,
          overflowY: "auto",
          padding: "20px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          scrollbarWidth: "thin",
          scrollbarColor: "#cbd5e1 transparent",
        }}
      >
        {messages.map((msg) =>
          msg.from === "bot" ? (
            <BotBubble key={msg.id} msg={msg} />
          ) : (
            <UserBubble key={msg.id} text={msg.text} />
          ),
        )}
        {typing && <TypingBubble />}
        <div ref={bottomRef} />
      </div>

      {/* ── SUGGESTIONS ── */}
      <div
        style={{
          flexShrink: 0,
          padding: "8px 16px",
          background: "#ffffff",
          borderTop: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: "0.63rem",
            color: ac,
            fontWeight: 700,
            flexShrink: 0,
            transition: "color 0.3s",
          }}
        >
          {curTab.icon}
        </span>
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            flex: 1,
            scrollbarWidth: "none",
          }}
        >
          {suggestions.map((s) => {
            const isG = s.includes("Graph");
            return (
              <button
                key={s}
                onClick={() => send(s)}
                style={{
                  flexShrink: 0,
                  padding: "5px 12px",
                  borderRadius: 99,
                  background: isG ? ac + "12" : "#f8fafc",
                  border: isG ? `1px solid ${ac}44` : "1px solid #e2e8f0",
                  color: isG ? ac : "#475569",
                  fontSize: "0.7rem",
                  fontWeight: isG ? 700 : 600,
                  cursor: "pointer",
                  fontFamily: "'Outfit',sans-serif",
                  transition: "all 0.18s",
                  outline: "none",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = ac + "12";
                  e.target.style.borderColor = ac + "44";
                  e.target.style.color = ac;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = isG ? ac + "12" : "#f8fafc";
                  e.target.style.borderColor = isG ? ac + "44" : "#e2e8f0";
                  e.target.style.color = isG ? ac : "#475569";
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── INPUT ── */}
      <div
        style={{
          flexShrink: 0,
          padding: "10px 16px 14px",
          background: "#ffffff",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "#f8fafc",
            border: "1.5px solid #e2e8f0",
            borderRadius: 14,
            padding: "8px 8px 8px 16px",
            transition: "border-color 0.2s, box-shadow 0.2s",
          }}
          onFocusCapture={(e) => {
            e.currentTarget.style.borderColor = ac;
            e.currentTarget.style.boxShadow = `0 0 0 3px ${ac}18`;
          }}
          onBlurCapture={(e) => {
            e.currentTarget.style.borderColor = "#e2e8f0";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={isLoading}
            placeholder={
              {
                bookings: "Appointments विचारा… (today, graph, service)",
                tasks: "Tasks विचारा… (technician, pending, graph)",
                ads: "Ads विचारा… (active, budget, type, graph)",
              }[mode]
            }
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#1e293b",
              fontSize: "0.84rem",
              fontFamily: "'Outfit',sans-serif",
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || isLoading}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: input.trim()
                ? `linear-gradient(135deg,${ac},${curTab.grad})`
                : "#f1f5f9",
              border: "none",
              cursor: input.trim() ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.2s",
              boxShadow: input.trim() ? `0 2px 10px ${ac}55` : "none",
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path
                d="M22 2L11 13"
                stroke={input.trim() ? "white" : "#94a3b8"}
                strokeWidth="2.2"
                strokeLinecap="round"
              />
              <path
                d="M22 2L15 22L11 13L2 9L22 2Z"
                stroke={input.trim() ? "white" : "#94a3b8"}
                strokeWidth="2.2"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div
          style={{
            textAlign: "center",
            marginTop: 7,
            fontSize: "0.62rem",
            color: "#000",
            letterSpacing: 0.5,
          }}
        >
          Powered by{" "}
          <span style={{ color: "#db25eb", fontWeight: 700 }}>COMTRANSE</span> ·
          Triple API Intelligence
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        html, body, #root { height: 100%; margin: 0; padding: 0; overflow: hidden; background: #f8fafc; }
        @keyframes bob { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        input::placeholder { color: #94a3b8; }
        div[style*="scrollbarWidth: none"]::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
